import React, { useMemo } from 'react';
import { Linking, StyleSheet, View } from 'react-native';
import { mvs } from 'react-native-size-matters';

import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import i18n from '../../localization/i18n';
import { SongHistoryEntry } from '../../models';
import {
  ContextualMenuItem,
  ContextualMenuListItem,
  ContextualMenuSeparator,
  ContextualMenuShell,
} from '../ContextualMenu';
import CustomText from '../CustomText';

interface SongHistoryContextualMenuProps {
  visible: boolean;
  onRequestClose: () => void;
  song: SongHistoryEntry | null;
}

export const SongHistoryContextualMenu: React.FC<SongHistoryContextualMenuProps> = ({
  visible,
  onRequestClose,
  song,
}) => {
  const emptyTextFontSize = useAdjustedFontSize(mvs(16));

  const menuItems: ContextualMenuItem[] = useMemo(() => {
    const allItems = [
      {
        title: 'Spotify',
        icon: 'spotify' as const,
        link: song?.spotify,
      },
      {
        title: 'Deezer',
        icon: 'deezer' as const,
        link: song?.deezer,
      },
      {
        title: 'Apple Music',
        icon: 'apple' as const,
        link: song?.applemusic,
      },
    ];

    // filter out items without links
    return allItems
      .filter(item => item.link)
      .map(item => ({
        title: item.title,
        icon: item.icon,
        onPress: () => {
          if (item.link) {
            Linking.openURL(item.link);
          }
        },
      }));
  }, [song]);

  return (
    <ContextualMenuShell visible={visible} onRequestClose={onRequestClose}>
      <View style={styles.container}>
        {menuItems.length > 0 ? (
          menuItems.map((item, index) => (
            <View key={item.title}>
              <ContextualMenuListItem item={item} onRequestClose={onRequestClose} />
              {index < menuItems.length - 1 && <ContextualMenuSeparator />}
            </View>
          ))
        ) : (
          <View style={styles.emptyState}>
            <CustomText
              style={[
                styles.emptyText,
                {
                  fontSize: emptyTextFontSize,
                },
              ]}
            >
              {i18n.t('songHistory.noLinksAvailable')}
            </CustomText>
          </View>
        )}
      </View>
    </ContextualMenuShell>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  emptyState: {
    paddingVertical: mvs(24),
    paddingHorizontal: mvs(16),
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    textAlign: 'center',
    opacity: 0.7,
  },
});
